import { Fragment, useState, useEffect, useMemo } from 'react';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { BarChart3, Layers, Package, Table2, TrendingUp } from 'lucide-react';
import { SELLIN_COLORS } from '../../config/colors';
import { MONTH_NAMES as MONTHS } from '../../config/months';
import { CountrySelect, Flag } from '../../components/Flag';

const fmtNum = (v) => {
  const n = Math.round(v || 0);
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
};
const fmtMoney = (v) => {
  const n = Math.round(v || 0);
  return n < 0 ? `-$${Math.abs(n).toLocaleString()}` : `$${n.toLocaleString()}`;
};
const shortSku = (name) => name.replace(/SEROQUEL/i, '').replace(/\(C1\)/i, '').replace(/\s+/g, ' ').trim();
const sumArr = (a) => (a || []).reduce((x, y) => x + (y || 0), 0);
const addArr = (a, b) => Array.from({ length: 12 }, (_, i) => (a?.[i] || 0) + (b?.[i] || 0));
const cloneMetric = (m) => Object.fromEntries(Object.entries(m || {}).map(([y, a]) => [y, [...a]]));

// Mexico: fold the "Mutual" channel into "Public" (sum same-named SKUs)
const mergeMutualIntoPublic = (country) => {
  const pubIdx = country.channels.findIndex(c => /públic|public/i.test(c.name));
  const mutIdx = country.channels.findIndex(c => /mutual/i.test(c.name));
  if (pubIdx < 0 || mutIdx < 0) return country;
  const map = new Map(country.channels[pubIdx].products.map(p => [p.name, { name: p.name, units: cloneMetric(p.units), values: cloneMetric(p.values) }]));
  country.channels[mutIdx].products.forEach(sp => {
    const tp = map.get(sp.name);
    if (tp) {
      Object.entries(sp.units || {}).forEach(([y, a]) => { tp.units[y] = addArr(tp.units[y], a); });
      Object.entries(sp.values || {}).forEach(([y, a]) => { tp.values[y] = addArr(tp.values[y], a); });
    } else {
      map.set(sp.name, { name: sp.name, units: cloneMetric(sp.units), values: cloneMetric(sp.values) });
    }
  });
  const mergedPublic = { ...country.channels[pubIdx], name: 'Public', products: [...map.values()] };
  const channels = country.channels.map((c, i) => (i === pubIdx ? mergedPublic : c)).filter((_, i) => i !== mutIdx);
  return { ...country, channels };
};

const YoY = ({ curr, prev }) => {
  if (prev == null || prev === 0) return <span className="text-xs text-slate-400">—</span>;
  const pct = ((curr - prev) / Math.abs(prev)) * 100;
  const pos = pct >= 0;
  return <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${pos ? 'text-emerald-600' : 'text-red-600'}`}>{pos ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}%</span>;
};

export const SellInModule = ({ sellInData }) => {
  const countries = useMemo(() => {
    const raw = sellInData?.countries || null;
    if (!raw) return null;
    const out = {};
    Object.entries(raw).forEach(([k, c]) => { out[k] = k === 'mexico' ? mergeMutualIntoPublic(c) : c; });
    return out;
  }, [sellInData]);
  const countryKeys = useMemo(() => (countries ? Object.keys(countries).sort((a, b) => countries[a].name.localeCompare(countries[b].name)) : []), [countries]);

  // Always start at LATAM on entry; a drill key (set elsewhere) is consumed once.
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const drill = localStorage.getItem('luye_sellin_country');
    if (drill) { localStorage.removeItem('luye_sellin_country'); return drill; }
    return 'latam';
  });
  const [metric, setMetricRaw] = useState(() => localStorage.getItem('luye_sellin_metric') || 'units');
  const setMetric = (v) => { localStorage.setItem('luye_sellin_metric', v); setMetricRaw(v); };
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [tableYear, setTableYear] = useState(null);

  const isLatam = selectedCountry === 'latam';
  const country = !isLatam ? countries?.[selectedCountry] : null;
  const canValues = !isLatam && !!country?.hasValues;
  const effectiveMetric = canValues ? metric : 'units';

  // Reset channel selection + table year when the country changes
  useEffect(() => {
    if (country) {
      setSelectedChannels(country.channels.map(c => c.name));
      setTableYear(Math.max(...country.years));
    }
  }, [selectedCountry]); // eslint-disable-line

  const channelColor = (name) => {
    const idx = country ? country.channels.findIndex(c => c.name === name) : 0;
    return SELLIN_COLORS[(idx < 0 ? 0 : idx) % SELLIN_COLORS.length];
  };

  // ---------- Per-country derived data ----------
  const countryView = useMemo(() => {
    if (!country) return null;
    const years = [...country.years].sort();
    const latestYear = years[years.length - 1];
    const prevYear = years.includes(latestYear - 1) ? latestYear - 1 : null;
    const mkey = effectiveMetric; // 'units' | 'values'

    const channelTotal = (ch, year, key) => ch.products.reduce((a, p) => a + sumArr(p[key]?.[year]), 0);

    // KPIs (whole country)
    const unitsLatest = country.channels.reduce((a, ch) => a + channelTotal(ch, latestYear, 'units'), 0);
    const unitsPrev = prevYear ? country.channels.reduce((a, ch) => a + channelTotal(ch, prevYear, 'units'), 0) : null;
    const valLatest = country.hasValues ? country.channels.reduce((a, ch) => a + channelTotal(ch, latestYear, 'values'), 0) : null;
    const valPrev = country.hasValues && prevYear ? country.channels.reduce((a, ch) => a + channelTotal(ch, prevYear, 'values'), 0) : null;
    const skuCount = new Set(country.channels.flatMap(ch => ch.products.map(p => p.name))).size;

    // Continuous monthly trend across all years (selected channels) + Total
    const trend = [];
    years.forEach(y => {
      for (let m = 0; m < 12; m++) {
        const row = { label: `${MONTHS[m]} '${String(y).slice(2)}` };
        let total = 0;
        country.channels.forEach(ch => {
          if (!selectedChannels.includes(ch.name)) return;
          const v = ch.products.reduce((a, p) => a + (p[mkey]?.[y]?.[m] || 0), 0);
          row[ch.name] = v;
          total += v;
        });
        row.Total = total;
        trend.push(row);
      }
    });
    // Trim leading/trailing empty months
    const nonEmpty = trend.map(r => r.Total !== 0);
    const first = nonEmpty.indexOf(true);
    const last = nonEmpty.lastIndexOf(true);
    const trendTrim = first < 0 ? [] : trend.slice(first, last + 1);

    // Fixed Y-axis max = highest monthly total across ALL channels (ignores the
    // channel filter) so selecting a small channel doesn't rescale the chart.
    let yMax = 0;
    years.forEach(y => {
      for (let m = 0; m < 12; m++) {
        let allTotal = 0;
        country.channels.forEach(ch => { allTotal += ch.products.reduce((a, p) => a + (p[mkey]?.[y]?.[m] || 0), 0); });
        if (allTotal > yMax) yMax = allTotal;
      }
    });
    yMax = yMax > 0 ? Math.ceil(yMax * 1.05) : 0;

    // Channel breakdown (FY latest) — share of total, by the active metric
    const useVal = effectiveMetric === 'values' && country.hasValues;
    const channelTotalUnits = country.channels.reduce((a, ch) => a + channelTotal(ch, latestYear, 'units'), 0);
    const channelTotalVal = country.hasValues ? country.channels.reduce((a, ch) => a + channelTotal(ch, latestYear, 'values'), 0) : 0;
    const channelRows = country.channels.map(ch => {
      const u = channelTotal(ch, latestYear, 'units');
      const v = country.hasValues ? channelTotal(ch, latestYear, 'values') : null;
      const numer = useVal ? v : u;
      const denom = useVal ? channelTotalVal : channelTotalUnits;
      return {
        name: ch.name,
        unitsLatest: u,
        valLatest: v,
        share: denom > 0 ? (numer / denom) * 100 : 0,
      };
    }).sort((a, b) => b.share - a.share);

    return { years, latestYear, prevYear, unitsLatest, unitsPrev, valLatest, valPrev, skuCount, trend: trendTrim, channelRows, yMax };
  }, [country, selectedChannels, effectiveMetric]);

  // ---------- LATAM derived data (units only) ----------
  const latamView = useMemo(() => {
    if (!isLatam || !countries) return null;
    // MAT (Moving Annual Total): May 2025 → Apr 2026
    const matSum = (c) => {
      const part = (year, from, to) => c.channels.reduce((a, ch) => a + ch.products.reduce((b, p) => {
        const arr = p.units?.[year];
        if (!arr) return b;
        let s = 0; for (let m = from; m <= to; m++) s += (arr[m] || 0);
        return b + s;
      }, 0), 0);
      return part(2025, 4, 11) + part(2026, 0, 3);
    };
    const rows = countryKeys.map(k => {
      const c = countries[k];
      return { key: k, name: c.name, mat: matSum(c) };
    }).sort((a, b) => b.mat - a.mat);
    const max = Math.max(1, ...rows.map(r => r.mat));

    // Trend: union of years, monthly units per country, on one continuous timeline.
    const allYears = [...new Set(countryKeys.flatMap(k => countries[k].years))].sort();
    const points = [];
    allYears.forEach(y => { for (let m = 0; m < 12; m++) points.push({ y, m, label: `${MONTHS[m]} '${String(y).slice(2)}` }); });
    // Flat units series per country across the timeline.
    const series = {};
    countryKeys.forEach(k => {
      const c = countries[k];
      series[c.name] = points.map(pt => c.channels.reduce((a, ch) => a + ch.products.reduce((b, p) => b + (p.units?.[pt.y]?.[pt.m] || 0), 0), 0));
    });
    // Outside each country's active range (before its first data point and after
    // its last), use null so the line shows a GAP instead of dropping to zero —
    // a month the Excel simply doesn't cover shouldn't look like a sales crash.
    // Zeros WITHIN the active range are kept (they are genuine).
    countryKeys.forEach(k => {
      const name = countries[k].name, arr = series[name];
      const first = arr.findIndex(v => v !== 0);
      const last = arr.length - 1 - [...arr].reverse().findIndex(v => v !== 0);
      series[name] = arr.map((v, i) => (first < 0 || i < first || i > last) ? null : v);
    });
    const trend = points.map((pt, i) => {
      const row = { label: pt.label };
      let total = 0;
      countryKeys.forEach(k => { const name = countries[k].name; const v = series[name][i]; row[name] = v; total += (v || 0); });
      row.__total = total;
      return row;
    });
    const ne = trend.map(r => r.__total !== 0);
    const f = ne.indexOf(true), l = ne.lastIndexOf(true);
    const trendTrim = f < 0 ? [] : trend.slice(f, l + 1);

    return { rows, max, trend: trendTrim };
  }, [isLatam, countries, countryKeys]);

  if (!countries || countryKeys.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-lg font-semibold text-slate-700">No Sell In data available</p>
          <p className="text-sm text-slate-500 mt-1">Upload a Sell In Excel file from the Admin panel to see data here.</p>
        </div>
      </div>
    );
  }

  const options = [{ value: 'latam', label: 'LATAM', country: 'latam' }, ...countryKeys.map(k => ({ value: k, label: countries[k].name, country: k }))];
  const toggleChannel = (name) => setSelectedChannels(prev => prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]);
  const fmtVal = (v) => `${country?.currency || ''} ${fmtNum(v)}`.trim();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-blue-600 mb-1">Channel performance</p>
          <h2 className="text-2xl sm:text-[28px] leading-tight font-bold text-slate-900 tracking-tight">Sell In</h2>
          <p className="text-sm text-slate-500 mt-1">Units sold from our partner to distributors by country and by channel (Units & Local currency)</p>
        </div>
      </div>

      {/* Country filter + metric toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 flex items-center gap-4 flex-wrap">
        <span className="text-sm text-slate-600">Country:</span>
        <CountrySelect value={selectedCountry} onChange={setSelectedCountry} options={options} />
        {!isLatam && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600 ml-2">Metric:</span>
            <div className="flex bg-slate-100 rounded-lg p-1 border border-slate-200">
              <button onClick={() => setMetric('units')} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${effectiveMetric === 'units' ? 'bg-blue-600 text-white shadow' : 'text-slate-600 hover:text-slate-800'}`}>Units</button>
              <button onClick={() => canValues && setMetric('values')} disabled={!canValues} className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${effectiveMetric === 'values' ? 'bg-blue-600 text-white shadow' : canValues ? 'text-slate-600 hover:text-slate-800' : 'text-slate-300 cursor-not-allowed'}`}>Values</button>
            </div>
          </div>
        )}
      </div>

      {isLatam ? (
        /* ===================== LATAM VIEW (units only) ===================== */
        <>
          {/* Country leaderboard */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Units by Country (MAT)</h3>
            <div className="flex items-center gap-4 px-2 mb-1">
              <div className="w-28 shrink-0" />
              <div className="flex-1" />
              <div className="w-20 text-right text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Units MAT</div>
            </div>
            <div className="divide-y divide-slate-100">
              {latamView.rows.map(r => (
                <button key={r.key} onClick={() => setSelectedCountry(r.key)} className="w-full flex items-center gap-4 py-2.5 text-left hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors">
                  <div className="w-28 shrink-0 flex items-center gap-2"><Flag country={r.key} size={16} /><span className="text-sm font-semibold text-slate-700">{r.name}</span></div>
                  <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${(r.mat / latamView.max) * 100}%` }}></div></div>
                  <div className="w-20 text-right text-sm font-semibold text-slate-700">{fmtNum(r.mat)}</div>
                </button>
              ))}
            </div>
          </div>

          {/* LATAM units trend */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Monthly Units Trend by Country</h3>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={latamView.trend} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={fmtNum} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n) => [fmtNum(v), n]} />
                <Legend />
                {latamView.rows.map((r, i) => (
                  <Line key={r.key} type="monotone" dataKey={r.name} stroke={SELLIN_COLORS[i % SELLIN_COLORS.length]} strokeWidth={2} dot={false} connectNulls={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        /* ===================== COUNTRY VIEW ===================== */
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Units FY{countryView.latestYear}</span><Package className="w-5 h-5 text-blue-500" /></div>
              <div className="text-2xl font-bold text-slate-800">{fmtNum(countryView.unitsLatest)}</div>
              <div className="mt-1"><YoY curr={countryView.unitsLatest} prev={countryView.unitsPrev} /> <span className="text-xs text-slate-400">vs FY{countryView.prevYear ?? '—'}</span></div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Value FY{countryView.latestYear}</span><TrendingUp className="w-5 h-5 text-green-500" /></div>
              <div className="text-2xl font-bold text-slate-800">{country.hasValues ? fmtVal(countryView.valLatest) : '—'}</div>
              <div className="mt-1">{country.hasValues ? <YoY curr={countryView.valLatest} prev={countryView.valPrev} /> : <span className="text-xs text-slate-400">no value data</span>}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Channels</span><Layers className="w-5 h-5 text-orange-500" /></div>
              <div className="text-2xl font-bold text-slate-800">{country.channels.length}</div>
              <div className="text-xs text-slate-400 truncate">{country.channels.map(c => c.name).join(', ')}</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-500">Distinct SKUs</span><BarChart3 className="w-5 h-5 text-purple-500" /></div>
              <div className="text-2xl font-bold text-slate-800">{countryView.skuCount}</div>
            </div>
          </div>

          {/* Channel filter chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-slate-500">Channels:</span>
            {country.channels.map(ch => {
              const on = selectedChannels.includes(ch.name);
              return (
                <button key={ch.name} onClick={() => toggleChannel(ch.name)} className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${on ? 'border-transparent text-white' : 'bg-white border-slate-300 text-slate-500'}`} style={on ? { backgroundColor: channelColor(ch.name) } : {}}>
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: on ? '#fff' : channelColor(ch.name) }}></span>{ch.name}
                </button>
              );
            })}
          </div>

          {/* Monthly trend */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Monthly {effectiveMetric === 'units' ? 'Units' : `Value (${country.currency})`} Trend</h3>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={countryView.trend} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={fmtNum} tick={{ fontSize: 11 }} domain={[0, countryView.yMax || 'auto']} allowDataOverflow />
                <Tooltip formatter={(v, n) => [effectiveMetric === 'values' ? fmtVal(v) : fmtNum(v), n]} />
                <Legend />
                {country.channels.filter(ch => selectedChannels.includes(ch.name)).map(ch => (
                  <Line key={ch.name} type="monotone" dataKey={ch.name} stroke={channelColor(ch.name)} strokeWidth={2} dot={false} />
                ))}
                <Line type="monotone" dataKey="Total" stroke="#1e293b" strokeWidth={2} strokeDasharray="5 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Channel breakdown — share of total */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">Channel Mix — FY{countryView.latestYear} {effectiveMetric === 'values' ? 'Value' : 'Units'} (% of total)</h3>
            <div className="divide-y divide-slate-100">
              {countryView.channelRows.map(c => (
                <div key={c.name} className="flex items-center gap-4 py-2.5">
                  <div className="w-28 shrink-0 flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: channelColor(c.name) }}></span><span className="text-sm font-semibold text-slate-700">{c.name}</span></div>
                  <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${c.share}%`, backgroundColor: channelColor(c.name) }}></div></div>
                  <div className="w-14 text-right text-sm font-bold text-slate-800">{c.share.toFixed(0)}%</div>
                  <div className="w-24 text-right text-xs text-slate-500">{effectiveMetric === 'values' ? fmtVal(c.valLatest) : `${fmtNum(c.unitsLatest)} u`}</div>
                </div>
              ))}
            </div>
          </div>

          {/* SKU table */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Table2 className="w-4 h-4" /> SKU Detail</h3>
              <div className="flex items-center gap-2">
                {country.years.length > 1 && (
                  <div className="flex bg-slate-100 rounded-lg p-1">
                    {[...country.years].sort().map(y => (
                      <button key={y} onClick={() => setTableYear(y)} className={`px-3 py-1 rounded-md text-xs font-medium ${tableYear === y ? 'bg-white shadow text-blue-600' : 'text-slate-600'}`}>{y}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b-2 border-slate-300">
                    <th className="text-left py-2.5 px-2 font-semibold text-slate-600 sticky left-0 bg-white min-w-40 z-10">SKU</th>
                    {MONTHS.map(m => <th key={m} className="text-right py-2.5 px-2 font-medium text-slate-600 whitespace-nowrap">{m} {String(tableYear).slice(2)}</th>)}
                    <th className="text-right py-2.5 px-2 font-bold text-slate-700 bg-blue-50 whitespace-nowrap">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {country.channels.filter(ch => selectedChannels.includes(ch.name)).map(ch => {
                    const key = (country.hasValues && effectiveMetric === 'values') ? 'values' : 'units';
                    return (
                      <Fragment key={ch.name}>
                        <tr className="bg-slate-50"><td colSpan={14} className="py-1.5 px-1 font-semibold text-slate-600 sticky left-0 bg-slate-50"><span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: channelColor(ch.name) }}></span>{ch.name}</span></td></tr>
                        {ch.products.map(p => {
                          const arr = p[key]?.[tableYear] || Array(12).fill(0);
                          const fy = sumArr(arr);
                          return (
                            <tr key={p.name} className="border-b border-slate-100 hover:bg-blue-50/40">
                              <td className="py-1.5 px-2 font-medium sticky left-0 bg-white border-r-2 border-slate-200 truncate max-w-40 z-10" title={p.name}>{shortSku(p.name)}</td>
                              {arr.map((v, i) => <td key={i} className={`text-right py-1.5 px-2 ${v < 0 ? 'text-red-500 font-medium' : v === 0 ? 'text-slate-300' : 'text-slate-700'}`}>{v !== 0 ? (key === 'values' ? fmtMoney(v) : Math.round(v).toLocaleString()) : '—'}</td>)}
                              <td className="text-right py-1.5 px-2 font-bold bg-blue-50 text-slate-800">{key === 'values' ? fmtMoney(fy) : Math.round(fy).toLocaleString()}</td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    );
                  })}
                  {(() => {
                    const tkey = (country.hasValues && effectiveMetric === 'values') ? 'values' : 'units';
                    const vis = country.channels.filter(ch => selectedChannels.includes(ch.name));
                    const totals = MONTHS.map((_, i) => vis.reduce((s, ch) => s + ch.products.reduce((ps, p) => ps + ((p[tkey]?.[tableYear] || [])[i] || 0), 0), 0));
                    const fyTotal = sumArr(totals);
                    return (
                      <tr className="border-t-2 border-slate-300 bg-blue-50/50 font-bold">
                        <td className="py-2 px-2 sticky left-0 bg-blue-50/50 border-r-2 border-slate-200 text-slate-800 z-10">Total</td>
                        {totals.map((v, i) => <td key={i} className={`text-right py-2 px-2 ${v < 0 ? 'text-red-500' : v === 0 ? 'text-slate-300' : 'text-slate-800'}`}>{v !== 0 ? (tkey === 'values' ? fmtMoney(v) : Math.round(v).toLocaleString()) : '—'}</td>)}
                        <td className="text-right py-2 px-2 bg-blue-100 text-slate-900">{tkey === 'values' ? fmtMoney(fyTotal) : Math.round(fyTotal).toLocaleString()}</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
